import type { WebTrpcClient } from '../trpc/client.js';
import type {
  AnalyticsConsentChoiceInput,
  AnalyticsConsentCurrent,
  AnalyticsConsentPort,
} from './AnalyticsConsentPort.js';

type AnalyticsConsentNamespace = NonNullable<
  NonNullable<WebTrpcClient['member']>['analyticsConsent']
>;

/** Cookie-authenticated adapter for the server-authoritative member choice. */
export class TrpcAnalyticsConsentAdapter implements AnalyticsConsentPort {
  readonly #client: WebTrpcClient;

  constructor(client: WebTrpcClient) {
    this.#client = client;
  }

  async current(): Promise<AnalyticsConsentCurrent> {
    return await this.#current().query();
  }

  async choose(input: AnalyticsConsentChoiceInput): Promise<AnalyticsConsentCurrent> {
    return await this.#choose().mutate(input);
  }

  #consent(): AnalyticsConsentNamespace {
    const consent = this.#client.member?.analyticsConsent;
    if (consent === undefined) {
      throw new Error(
        'TrpcAnalyticsConsentAdapter: member.analyticsConsent is absent on the AppRouter — ' +
          'member analytics consent requires the 047 API activation.',
      );
    }
    return consent;
  }

  #current(): NonNullable<AnalyticsConsentNamespace['current']> {
    const current = this.#consent().current;
    if (current === undefined) {
      throw new Error('TrpcAnalyticsConsentAdapter: member.analyticsConsent.current is absent');
    }
    return current;
  }

  #choose(): NonNullable<AnalyticsConsentNamespace['choose']> {
    const choose = this.#consent().choose;
    if (choose === undefined) {
      throw new Error('TrpcAnalyticsConsentAdapter: member.analyticsConsent.choose is absent');
    }
    return choose;
  }
}
